import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store';

import { fetchFiles, setIngestPolling } from '../../store/slices/filesSlice';
import { fileEndpoints } from '../../services/api/endpoints/file.endpoint';
import { KnowledgeBaseUrls, KnowledgeBaseUrlItem, ParsedCrawlStatus } from '../../types/file';

// ------------------------------------------------------------------
export const useFiles = () => {
  const dispatch = useDispatch<AppDispatch>();

  // Redux slice state
  const files = useSelector((s: RootState) => s.files.files);
  const ingestPolling = useSelector((s: RootState) => s.files.ingestPolling);

  // local component-level state
  const [knowledgeBaseUrls, setKnowledgeBaseUrls] = useState<string[]>([]);
  const [knowledgeBaseUrlItems, setKnowledgeBaseUrlItems] = useState<KnowledgeBaseUrlItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<Record<string, boolean>>({});

  const selectedOrganizationId = useSelector(
    (state: RootState) => state.organizationContext.selectedOrganizationId
  );

  // 1. files list (now lives in Redux)
  const refetch = useCallback(() => {
    if (!selectedOrganizationId) return Promise.resolve();
    return dispatch(fetchFiles(selectedOrganizationId));
  }, [dispatch, selectedOrganizationId]);

  // 2. global ingest-polling helpers
  const startIngestPolling = () => dispatch(setIngestPolling(true));
  const stopIngestPolling = () => dispatch(setIngestPolling(false));

  const ingestFiles = async () => {
    if (!selectedOrganizationId) throw new Error('No organization selected');
    setIsLoading(true);
    try {
      await fileEndpoints.ingestFiles(selectedOrganizationId);
      await fileEndpoints.ingestWebFiles(selectedOrganizationId);
    } finally {
      setIsLoading(false);
    }
  };

  const createKnowledgeBaseUrls = async (data: KnowledgeBaseUrls) => {
    if (!selectedOrganizationId) throw new Error('No organization selected');
    setIsLoading(true);
    try {
      return await fileEndpoints.createKnowledgeBaseUrls(data);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteFile = async (fileId: string) => {
    if (!selectedOrganizationId) throw new Error('No organization selected');
    setIsLoading(true);
    try {
      const file = files.find(f => f.id === fileId);
      if (!file) throw new Error('File not found');
      await fileEndpoints.deleteFile(file.key);
      await Promise.all([refetch(), fetchUrls()]); // refresh both lists
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUrls = useCallback(async () => {
    if (!selectedOrganizationId) return;
    setIsLoading(true);
    try {
      const data = await fileEndpoints.getKnowledgeBaseUrls(selectedOrganizationId);
      // Extract URL items with status_id from parent item
      // Response format: [{ serviceType, urls: [{url}], status_id, uuid }]
      const urlItems: KnowledgeBaseUrlItem[] = data.flatMap((item: any) =>
        item.urls.map((u: any) => ({
          url: u.url,
          status_id: item.status_id, // status_id is at parent level, not URL level
        }))
      );
      // Also keep the flat URL strings for backwards compatibility
      const urls = urlItems.map(item => item.url);
      setKnowledgeBaseUrls(urls);
      setKnowledgeBaseUrlItems(urlItems);
      return urlItems; // Return for use in checking crawl status
    } catch (err: any) {
      setError(err.message || 'Failed to fetch URLs');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [selectedOrganizationId]);

  const downloadFile = useCallback(
    async (fileName: string) => {
      if (!selectedOrganizationId) throw new Error('No organization selected');
      setDownloading(prev => ({ ...prev, [fileName]: true }));
      try {
        const url = await fileEndpoints.downloadFile(selectedOrganizationId, fileName);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } finally {
        setDownloading(prev => ({ ...prev, [fileName]: false }));
      }
    },
    [selectedOrganizationId]
  );

  const deleteKnowledgeBaseUrl = async (url: string) => {
    if (!selectedOrganizationId) throw new Error('No organization selected');
    setIsLoading(true);
    try {
      const response = await fileEndpoints.deleteKnowledgeBaseUrls(selectedOrganizationId, url);
      await Promise.all([refetch(), fetchUrls()]); // refresh both lists
      return response;
    } catch (err: any) {
      // Handle 404 - URL not found
      if (err.response?.status === 404) {
        throw new Error('URL not found in knowledge base');
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const checkCrawlStatus = useCallback(
    async (uuid: string): Promise<ParsedCrawlStatus> => {
      if (!selectedOrganizationId) throw new Error('No organization selected');
      return await fileEndpoints.getCrawlStatus(selectedOrganizationId, uuid);
    },
    [selectedOrganizationId]
  );

  // ----------------------------------------------------------------
  return {
    files,
    knowledgeBaseUrls,
    knowledgeBaseUrlItems,
    isLoading,
    ingestPolling,
    setIngestPolling,
    startIngestPolling,
    stopIngestPolling,
    error,
    refetch,
    refetchUrls: fetchUrls,
    deleteFile,
    deleteKnowledgeBaseUrl,
    ingestFiles,
    createKnowledgeBaseUrls,
    checkCrawlStatus,
    downloadFile,
    downloading,
  };
};
